import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { startOfLocalWeek } from '../../lib/raidCalendar'
import { BoardMode as BoardModeValue } from '../../types'
import type {
  BoardMode,
  RaidBoardPayload,
  RaidGroup,
  RaidId,
  RaidPlayer,
  RaidRoom,
  RaidStage,
  RaidStageDiff,
  RaidStageId,
  RaidSummary,
} from '../../types'

export interface RaidStateApi {
  readonly raids: Ref<ReadonlyArray<RaidSummary>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeRaidBoard: Ref<RaidBoardPayload | null>
  readonly boardMode: ComputedRef<BoardMode>
  readonly raidRooms: Ref<ReadonlyArray<RaidRoom>>
  readonly raidGroups: Ref<ReadonlyArray<RaidGroup>>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly raidStageDiffs: Ref<ReadonlyArray<RaidStageDiff>>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly stageSelectionPending: Ref<boolean>
  readonly calendarViewOpen: Ref<boolean>
  readonly calendarWindowStart: Ref<Date>
  readonly raidsForWindow: Ref<ReadonlyArray<RaidSummary>>
}

export function useRaidState(): RaidStateApi {
  const raids = ref<ReadonlyArray<RaidSummary>>([])
  const activeRaidId = ref<RaidId | null>(null)
  const activeRaidBoard = ref<RaidBoardPayload | null>(null)
  const boardMode = computed<BoardMode>(() =>
    activeRaidId.value === null ? BoardModeValue.Live : BoardModeValue.Raid,
  )
  const raidRooms = ref<ReadonlyArray<RaidRoom>>([])
  const raidGroups = ref<ReadonlyArray<RaidGroup>>([])
  const raidPlayers = ref<ReadonlyArray<RaidPlayer>>([])
  const raidStages = ref<ReadonlyArray<RaidStage>>([])
  const raidStageDiffs = ref<ReadonlyArray<RaidStageDiff>>([])
  const activeStageId = ref<RaidStageId | null>(null)
  const stageSelectionPending = ref<boolean>(false)
  const calendarWindowStart = ref<Date>(startOfLocalWeek(new Date()))
  const raidsForWindow = ref<ReadonlyArray<RaidSummary>>([])
  const calendarViewOpen = ref<boolean>(false)

  return {
    raids,
    activeRaidId,
    activeRaidBoard,
    boardMode,
    raidRooms,
    raidGroups,
    raidPlayers,
    raidStages,
    raidStageDiffs,
    activeStageId,
    stageSelectionPending,
    calendarViewOpen,
    calendarWindowStart,
    raidsForWindow,
  }
}
